import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Search, X } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import {
  CONTRACT_TAG_LABELS,
  CERT_TAG_LABELS,
  dealerTagLabel,
  dealerTagCategoryClassName,
} from "@/components/DealerTagBadges";

function TagChip({ tag, label }) {
  return (
    <span
      className={`rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider ${dealerTagCategoryClassName(tag)}`}
    >
      {label}
    </span>
  );
}

/** Top-of-page filter row for the Dealers list. */
export function DealersToolbar({
  q, setQ,
  predefined, setPredefined,
  activationId, setActivationId, activations,
  selectedServices,
  selectedTags, toggleTag, tagOptions,
  tagMatchMode, setTagMatchMode,
  statusFilter, setStatusFilter,
  anyColumnFilterActive, onClearAll,
}) {
  const { t } = useI18n();

  return (
    <>
      <div className="mb-4 grid grid-cols-1 gap-2 md:grid-cols-[minmax(240px,1fr)_220px_220px_180px_auto]">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="dealers-search"
            placeholder={t("common.search")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9"
          />
        </div>

        <Select value={predefined} onValueChange={setPredefined}>
          <SelectTrigger data-testid="dealers-predefined-filter">
            <SelectValue placeholder={t("dealers.filterPredefined")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">— {t("dealers.filterPredefined")}</SelectItem>
            <SelectItem value="activation" data-testid="pf-activation">
              {t("dealers.filterActivation")}
            </SelectItem>
            <SelectItem value="missing-coords" data-testid="pf-missing-coords">
              {t("dealers.filterMissingCoords")}
            </SelectItem>
          </SelectContent>
        </Select>

        {predefined === "activation" ? (
          <Select value={activationId} onValueChange={setActivationId}>
            <SelectTrigger data-testid="dealers-activation-select">
              <SelectValue placeholder="Choose activation…" />
            </SelectTrigger>
            <SelectContent>
              {activations.map((a) => (
                <SelectItem key={a.id} value={a.id} data-testid={`activation-opt-${a.id}`}>
                  {a.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <div />
        )}

        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" data-testid="dealers-tags-filter">
              {t("dealers.filterTags")}
              {selectedTags.length > 0 && (
                <span className="ml-2 rounded-full bg-primary px-1.5 py-[1px] font-mono text-[10px] text-primary-foreground">
                  {selectedTags.length}
                </span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="max-h-96 w-80 overflow-y-auto">
            <div className="mb-3 border-b border-border pb-3">
              <p className="data-label mb-2">Match</p>
              <div className="flex gap-4">
                <label className="flex cursor-pointer items-center gap-1.5 text-sm" data-testid="tags-match-all">
                  <input
                    type="radio"
                    name="tags-match-mode"
                    checked={tagMatchMode === "all"}
                    onChange={() => setTagMatchMode("all")}
                  />
                  All (AND)
                </label>
                <label className="flex cursor-pointer items-center gap-1.5 text-sm" data-testid="tags-match-any">
                  <input
                    type="radio"
                    name="tags-match-mode"
                    checked={tagMatchMode === "any"}
                    onChange={() => setTagMatchMode("any")}
                  />
                  Any (OR)
                </label>
              </div>
            </div>
            {[
              ["Contract", tagOptions.contract, (tag) => CONTRACT_TAG_LABELS[tag.slice("Contract.".length)] || tag],
              ["Services", tagOptions.services, (tag) => dealerTagLabel(tag)],
              ["Certifications", tagOptions.certifications, (tag) => CERT_TAG_LABELS[tag.slice("Cert.".length)] || tag],
            ].map(([groupLabel, groupTags, labelFor]) =>
              groupTags?.length ? (
                <div key={groupLabel} className="mb-3 last:mb-0">
                  <p className="data-label mb-2">{groupLabel}</p>
                  <div className="space-y-2">
                    {groupTags.map((tag) => (
                      <label
                        key={tag}
                        className="flex cursor-pointer items-center gap-2 text-sm"
                        data-testid={`tag-toggle-${tag}`}
                      >
                        <Checkbox checked={selectedTags.includes(tag)} onCheckedChange={() => toggleTag(tag)} />
                        <TagChip tag={tag} label={labelFor(tag)} />
                      </label>
                    ))}
                  </div>
                </div>
              ) : null,
            )}
            {tagOptions.vz?.length ? (
              <div>
                <p className="data-label mb-2">VZ Centers</p>
                <div className="space-y-2">
                  {tagOptions.vz.map(({ tag, name }) => (
                    <label
                      key={tag}
                      className="flex cursor-pointer items-center gap-2 text-sm"
                      data-testid={`tag-toggle-${tag}`}
                    >
                      <Checkbox checked={selectedTags.includes(tag)} onCheckedChange={() => toggleTag(tag)} />
                      <TagChip tag={tag} label={name} />
                    </label>
                  ))}
                </div>
              </div>
            ) : null}
          </PopoverContent>
        </Popover>

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="dealers-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active" data-testid="status-active">
              {t("dealers.filterStatusActive")}
            </SelectItem>
            <SelectItem value="deleted" data-testid="status-deleted">
              {t("dealers.filterStatusDeleted")}
            </SelectItem>
            <SelectItem value="all" data-testid="status-all">
              {t("dealers.filterStatusAll")}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {(q || predefined !== "none" || selectedServices.length > 0 || selectedTags.length > 0 || statusFilter !== "active" || anyColumnFilterActive) && (
        <Button
          onClick={onClearAll}
          data-testid="dealers-clear-all"
          variant="outline"
          size="sm"
          className="mb-3 gap-1.5 self-start"
        >
          <X className="h-3.5 w-3.5" />
          {t("dealers.clearFiltering")}
        </Button>
      )}
    </>
  );
}
